import React, { useEffect, useState, useRef, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Bar, Doughnut } from 'react-chartjs-2'
import {
  Chart as ChartJS,
  BarElement,
  ArcElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
} from 'chart.js'
import { Modal } from '@mui/material'
import classnames from 'classnames'
import Header from '../../components/Header/Header'
import PortalNavHeader from '../../components/PortalNavHeader/PortalNavHeader'
import { useMyContextLoading } from '../../components/LoadingContextProvider/LoadingContextProvider'
import { showSnackBar } from '../../components/Snackbar/Snackbar'
import { useThemeContext } from '../../theme/ThemeContext'
import {
  getPortalOverview,
  getPortalPayments,
  getPortalAccounts,
  getPortalDocuments,
  getPortalBudget,
} from '../../features/portal/portal'
import { formatMoney } from '../../utils/amount'
import { formatDateField } from '../../utils/date'
import ViewPdf from '../../components/ViewPdf/ViewPdf'
import Spinner from '../../components/Spiner/Spiner'
import apiRequest from '../../lib/http'
import { LuX } from 'react-icons/lu'
import PortalPaymentsTable from '../../components/Portal/PortalPaymentsTable'
import PortalRequestModal from './PortalRequestModal'
import PortalPaymentMethods from './PortalPaymentMethods'
import PortalHelpLine from '../../components/PortalBrand/PortalHelpLine'
import { usePortalBrandContext } from '../../components/PortalBrand/PortalBrandContext'
import { PORTAL_BRAND_STATUS } from '../../components/PortalBrand/portalBrandStatus'
import CreditScores from '../../components/CreditScores/CreditScores'
import { hasScoredReport } from '../../components/CreditScores/cardModel'
import { getPortalCreditScores } from '../../features/creditScores/creditScores'
import { selectFeatureFlag } from '../../features/featureFlags/selectors'
import { PORTAL_CREDIT_SCORES, PORTAL_DASHBOARD_FIXES } from '../../config/featureFlagKeys'
import { REQUEST_ERRORS_FILES } from '../../constants/requestErrorFlags'
import { normalizeError } from '../../utils/errorHelpers'
import { frequencyLabel, paymentsLabel } from '../../config/consts'
import { storedRole } from '../../features/notifications/audience'

ChartJS.register(BarElement, ArcElement, CategoryScale, LinearScale, Tooltip, Legend)

const SECTIONS = [
  { id: 'overview', label: 'Overview' },
  { id: 'payment-methods', label: 'Payment Methods' },
  { id: 'accounts', label: 'Accounts' },
  { id: 'budget', label: 'Budget' },
  { id: 'payments', label: 'Payments' },
  { id: 'documents', label: 'Documents' },
]

const frequencyText = (code) => frequencyLabel(code) || code

const documentDate = (doc) => new Date(doc.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })

const AccountStatusPill = ({ account }) => (
  <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-[var(--color-gray-200)] text-[var(--color-gray-700)]">
    {account.accountStatus?.label || 'Unknown'}
  </span>
)

const DocumentKindPill = ({ doc, className }) => (
  <span className={classnames(className, 'text-xs font-medium px-2 py-0.5 rounded', doc.documentKind === 'signDocument' ? 'bg-[color-mix(in_srgb,var(--color-primary-main)_10%,transparent)] text-primary-main' : 'bg-[var(--color-gray-200)] text-text-secondary')}>
    {doc.documentKind === 'signDocument' ? 'E-Sign' : 'Upload'}
  </span>
)

const PortalDashboard = () => {
  const dispatch = useDispatch()
  const { triggerLoading } = useMyContextLoading()
  const { mode } = useThemeContext()
  const portalBrand = usePortalBrandContext()
  const creditScoresOn = useSelector(selectFeatureFlag(PORTAL_CREDIT_SCORES))
  const filesStrict = useSelector(selectFeatureFlag(REQUEST_ERRORS_FILES))
  const fixesOn = useSelector(selectFeatureFlag(PORTAL_DASHBOARD_FIXES))
  const tableCardOverflow = fixesOn ? 'overflow-x-auto overflow-y-hidden' : 'overflow-hidden'
  const accountsWideCell = fixesOn ? 'hidden sm:table-cell' : ''
  const documentsWideCell = fixesOn ? 'hidden lg:table-cell' : ''
  const docViewerBox = fixesOn
    ? 'bg-surface rounded-2xl fixed flex-col justify-between py-3 max-h-content max-w-[min(880px,calc(100vw_-_32px))] min-h-[min(580px,calc(100vh_-_32px))] w-content min-w-[min(480px,calc(100vw_-_32px))]'
    : 'bg-surface rounded-2xl fixed flex-col justify-between py-3 max-h-content max-w-[880px] min-h-[580px] w-content min-w-[480px]'
  const isClient = storedRole() === 'contact'
  const [creditScores, setCreditScores] = useState(null)

  const [overview, setOverview] = useState(null)
  const [payments, setPayments] = useState(null)
  const [accounts, setAccounts] = useState(null)
  const [documents, setDocuments] = useState(null)
  const [budget, setBudget] = useState(null)
  const [activeSection, setActiveSection] = useState('overview')
  const [requestModalOpen, setRequestModalOpen] = useState(false)

  // Accounts view toggle

  // Documents modal state
  const [docModalOpen, setDocModalOpen] = useState(false)
  const [docModalContent, setDocModalContent] = useState('')
  const [docModalLoading, setDocModalLoading] = useState(false)
  const [docModalFileType, setDocModalFileType] = useState('')
  const [heightModalContent, setHeightModalContent] = useState(580)

  const contentRef = useRef(null)
  const paymentMethodsRef = useRef(null)
  const clickedSectionRef = useRef(null)

  useEffect(() => {
    const fetchAll = async () => {
      triggerLoading(true)
      try {
        const [ovRes, payRes, accRes, docRes, budRes] = await Promise.all([
          dispatch(getPortalOverview()),
          dispatch(getPortalPayments()),
          dispatch(getPortalAccounts()),
          dispatch(getPortalDocuments()),
          dispatch(getPortalBudget()),
        ])
        if (ovRes?.payload?.data) setOverview(ovRes.payload.data)
        if (payRes?.payload?.data) setPayments(payRes.payload.data)
        if (accRes?.payload?.data) setAccounts(accRes.payload.data)
        if (docRes?.payload?.data) setDocuments(docRes.payload.data)
        if (budRes?.payload?.data) setBudget(budRes.payload.data)
      } catch (error) {
        showSnackBar('error', 'Failed to load portal data')
      }
      triggerLoading(false)
    }
    fetchAll()
  }, [dispatch, triggerLoading])

  useEffect(() => {
    if (!creditScoresOn || !isClient) {
      setCreditScores(null)
      return undefined
    }
    let cancelled = false
    const loadCreditScores = async () => {
      const result = await dispatch(getPortalCreditScores())
      if (cancelled || !result || result.error || !result.payload) return
      setCreditScores(result.payload)
    }
    loadCreditScores()
    return () => {
      cancelled = true
    }
  }, [creditScoresOn, isClient, dispatch])

  const showCreditScores = Boolean(creditScores && creditScores.reportDayCount > 0 && hasScoredReport(creditScores))
  const sections = useMemo(() => (
    showCreditScores
      ? [SECTIONS[0], { id: 'credit-scores', label: 'Credit Scores' }, ...SECTIONS.slice(1)]
      : SECTIONS
  ), [showCreditScores])

  // Scroll spy
  useEffect(() => {
    const container = contentRef.current
    if (!container) return

    const handleScroll = () => {
      if (fixesOn && container.scrollHeight - container.scrollTop - container.clientHeight <= 1) {
        setActiveSection(clickedSectionRef.current || sections[sections.length - 1].id)
        return
      }
      for (const section of sections) {
        const el = document.getElementById(section.id)
        if (el) {
          const rect = el.getBoundingClientRect()
          if (rect.top <= 200 && rect.bottom > 200) {
            setActiveSection(section.id)
            break
          }
        }
      }
    }
    const forgetClickedSection = () => {
      clickedSectionRef.current = null
    }

    container.addEventListener('scroll', handleScroll)
    if (fixesOn) {
      container.addEventListener('wheel', forgetClickedSection, { passive: true })
      container.addEventListener('pointerdown', forgetClickedSection)
      window.addEventListener('keydown', forgetClickedSection)
    }
    return () => {
      container.removeEventListener('scroll', handleScroll)
      container.removeEventListener('wheel', forgetClickedSection)
      container.removeEventListener('pointerdown', forgetClickedSection)
      window.removeEventListener('keydown', forgetClickedSection)
    }
  }, [sections, fixesOn])

  const scrollToSection = (sectionId) => {
    const el = document.getElementById(sectionId)
    if (el) {
      if (fixesOn) clickedSectionRef.current = sectionId
      el.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }

  // Document helpers
  const adjustHeight = () => {
    const maxHeight = 780
    const currentHeight = window.innerHeight
    if (currentHeight < 819) {
      const newHeight = Math.round((currentHeight / 795) * (maxHeight - 120))
      setHeightModalContent(Math.min(newHeight, maxHeight))
    } else {
      setHeightModalContent(maxHeight)
    }
  }

  useEffect(() => {
    adjustHeight()
    window.addEventListener('resize', adjustHeight)
    return () => window.removeEventListener('resize', adjustHeight)
  }, [])

  const fetchDocumentBase64 = async (docId) => {
    const result = await apiRequest.get(`/documents/${docId}/download`)
    return result.data?.data
  }

  const getFileExtension = (name) => {
    if (!name) return ''
    return name.split('.').pop().toLowerCase()
  }

  const isImageFile = (ext) => ['png', 'jpg', 'jpeg'].includes(ext)

  const handleDocView = async (docId, docName) => {
    setDocModalOpen(true)
    setDocModalLoading(true)
    setDocModalContent('')
    setDocModalFileType(getFileExtension(docName))
    try {
      const base64Data = await fetchDocumentBase64(docId)
      if (!base64Data) {
        showSnackBar('error', 'Failed to open document')
        setDocModalOpen(false)
        return
      }
      setDocModalContent(base64Data)
    } catch (error) {
      showSnackBar('error', filesStrict ? normalizeError(error) : 'Failed to open document')
      setDocModalOpen(false)
    }
    setDocModalLoading(false)
  }

  const handleDocDownload = async (docId, fileName) => {
    try {
      const base64Data = await fetchDocumentBase64(docId)
      if (!base64Data) {
        showSnackBar('error', 'Failed to download document')
        return
      }
      const link = document.createElement('a')
      link.href = base64Data
      link.setAttribute('download', fileName || 'document')
      document.body.appendChild(link)
      link.click()
      link.remove()
    } catch (error) {
      showSnackBar('error', filesStrict ? normalizeError(error) : 'Failed to download document')
    }
  }

  const handleCloseDocModal = () => {
    setDocModalOpen(false)
    setDocModalContent('')
  }

  // Chart helpers
  const getGreeting = () => {
    const hour = new Date().getHours()
    if (hour < 12) return 'Good Morning'
    if (hour < 18) return 'Good Afternoon'
    return 'Good Evening'
  }

  const tickColor = mode === 'dark' ? '#EDEDED' : '#18181B'

  return (
    <>
      {/* Header */}
      <header className="sticky top-0 bg-paper z-10 shadow-sm">
        <Header />
        <PortalNavHeader />
      </header>

      <div className="flex" style={{ height: 'calc(100vh - 120px - 48px)' }}>
        {/* Sidebar Navigation */}
        <nav className="hidden md:flex flex-col w-48 shrink-0 border-r border-divider bg-paper py-6 px-3">
          {sections.map((section) => (
            <button
              key={section.id}
              onClick={() => scrollToSection(section.id)}
              className={`text-left text-sm px-3 py-2 rounded-lg mb-1 transition-colors ${
                activeSection === section.id
                  ? 'bg-[color-mix(in_srgb,var(--color-primary-main)_10%,transparent)] text-primary-main font-medium'
                  : 'text-text-secondary hover:bg-black/[0.04] dark:hover:bg-white/[0.04]'
              }`}
            >
              {section.label}
            </button>
          ))}
        </nav>

        {/* Main Content */}
        <div ref={contentRef} className="flex-1 overflow-y-auto px-6 md:px-10 py-8 pb-24">

          {/* === OVERVIEW SECTION === */}
          <section id="overview" className="mb-10">
            <div className="pb-7">
              <h4 className="text-xl font-heading-3 font-sans">
                {getGreeting()}, {overview?.user?.firstName || 'Client'}
              </h4>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
              <div className="border border-divider rounded-2xl p-5 bg-surface shadow-subtle dark:shadow-none">
                <p className="font-semibold text-[14px] text-text-secondary">Next Payment</p>
                <p className="font-bold text-[32px] pt-4 text-text-primary">
                  {overview?.nextPayment?.amount ? formatMoney(overview.nextPayment.amount) : 'N/A'}
                </p>
                {overview?.nextPayment?.dueDate && (
                  <p className="text-[14px] text-text-secondary pt-1">
                    Due {formatDateField(overview.nextPayment.dueDate, 'MMM D, YYYY')}
                  </p>
                )}
              </div>
              <div className="border border-divider rounded-2xl p-5 bg-surface shadow-subtle dark:shadow-none">
                <p className="font-semibold text-[14px] text-text-secondary">Active Plan</p>
                <p className="font-bold text-[32px] pt-4 text-text-primary">
                  {overview?.activePlan?.amount ? formatMoney(overview.activePlan.amount) : 'N/A'}
                </p>
                {overview?.activePlan?.frequency && (
                  <p className="text-[14px] text-text-secondary pt-1 capitalize">
                    {fixesOn ? frequencyText(overview.activePlan.frequency) : overview.activePlan.frequency} | {overview.activePlan.status}
                  </p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
              <div className="border border-divider rounded-2xl p-5 bg-surface shadow-subtle dark:shadow-none">
                <p className="font-semibold text-[14px] text-text-secondary">Total Program</p>
                <p className="font-bold text-[32px] pt-4 text-text-primary">
                  {formatMoney(payments?.summary?.totalProgramAmount || 0)}
                </p>
              </div>
              <div className="border border-divider rounded-2xl p-5 bg-surface shadow-subtle dark:shadow-none">
                <p className="font-semibold text-[14px] text-text-secondary">Amount Paid</p>
                <p className="font-bold text-[32px] pt-4 text-success-main">
                  {formatMoney(payments?.summary?.amountPaid || 0)}
                </p>
              </div>
              <div className="border border-divider rounded-2xl p-5 bg-surface shadow-subtle dark:shadow-none">
                <p className="font-semibold text-[14px] text-text-secondary">Remaining</p>
                <p className="font-bold text-[32px] pt-4 text-warning-main">
                  {formatMoney(payments?.summary?.remainingBalance || 0)}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
              <div className="border border-divider rounded-2xl p-5 bg-surface shadow-subtle dark:shadow-none">
                <p className="font-semibold text-[14px] text-text-primary mb-4">Enrolled Accounts</p>
                <div className="h-[250px]">
                  {overview?.enrolledAccountsChart?.length > 0 ? (
                    <Bar
                      key={mode}
                      data={{
                        labels: overview.enrolledAccountsChart.map(a =>
                          a.creditorName?.length > 15 ? a.creditorName.slice(0, 15) + '...' : a.creditorName
                        ),
                        datasets: [{
                          label: 'Balance',
                          data: overview.enrolledAccountsChart.map(a => a.balanceAmount),
                          backgroundColor: '#5E6AD2',
                          borderRadius: 6,
                          maxBarThickness: 40,
                          barPercentage: 0.7,
                        }],
                      }}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        indexAxis: 'y',
                        plugins: {
                          legend: { display: false },
                          tooltip: { callbacks: { label: (ctx) => formatMoney(ctx.raw) } },
                        },
                        scales: {
                          x: {
                            grid: { display: false },
                            ticks: { color: tickColor, callback: (v) => '$' + v.toLocaleString() },
                            border: { color: 'transparent' },
                          },
                          y: {
                            grid: { display: false },
                            ticks: { color: tickColor },
                            border: { color: 'transparent' },
                          },
                        },
                      }}
                    />
                  ) : (
                    <div className="h-full flex items-center justify-center text-text-secondary">
                      No enrolled accounts
                    </div>
                  )}
                </div>
              </div>

              <div className="border border-divider rounded-2xl p-5 bg-surface shadow-subtle dark:shadow-none">
                <p className="font-semibold text-[14px] text-text-primary mb-4">Account Activity</p>
                <div className="h-[250px] flex items-center justify-center">
                  {overview?.accountActivityChart?.length > 0 ? (
                    <Doughnut
                      key={mode}
                      data={{
                        labels: overview.accountActivityChart.map(a => a.label),
                        datasets: [{
                          data: overview.accountActivityChart.map(a => a.count),
                          backgroundColor: ['#5E6AD2', '#12B76A', '#F79009', '#F04438', '#6172F3', '#98A2B3'],
                          borderWidth: 0,
                        }],
                      }}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                          legend: {
                            position: 'bottom',
                            labels: {
                              color: tickColor,
                              padding: 16,
                              usePointStyle: true,
                              pointStyle: 'circle',
                            },
                          },
                        },
                      }}
                    />
                  ) : (
                    <div className="text-text-secondary">No activity data</div>
                  )}
                </div>
              </div>
            </div>
          </section>

          {showCreditScores && (
            <section id="credit-scores" className="mb-10">
              <CreditScores data={creditScores} />
            </section>
          )}

          {/* === PAYMENT METHODS SECTION === */}
          <section id="payment-methods" className="mb-10">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-text-primary">
                Payment Methods
              </h2>
              <button
                onClick={() => paymentMethodsRef.current?.openModal()}
                className="px-4 py-2 bg-primary-main text-primary-contrast text-sm font-medium rounded-xl hover:opacity-90"
              >
                Add Payment Method
              </button>
            </div>
            <PortalPaymentMethods ref={paymentMethodsRef} hideHeader />
          </section>

          {/* === ACCOUNTS SECTION === */}
          <section id="accounts" className="mb-10">
            <div className="mb-4">
              <h2 className="text-lg font-semibold text-text-primary">Accounts</h2>
              <div className="flex gap-6 mt-1">
                <div>
                  <span className="text-[13px] text-text-secondary">Total Enrolled Debt: </span>
                  <span className="text-md font-semibold text-text-primary">{formatMoney(accounts?.totalEnrolledDebt || 0)}</span>
                </div>
                <div>
                  <span className="text-[13px] text-text-secondary">Accounts: </span>
                  <span className="text-md font-semibold text-text-primary">{accounts?.totalAccounts || 0}</span>
                </div>
              </div>
            </div>

            {accounts?.accounts?.length === 0 && (
              <div className="py-10 text-center text-text-secondary text-sm">No enrolled accounts</div>
            )}

            {accounts?.accounts?.length > 0 && (
              <div className={classnames('border border-divider rounded-2xl bg-surface shadow-subtle dark:shadow-none', tableCardOverflow)}>
                <table className="w-full">
                  <thead className="bg-paper">
                    <tr>
                      <th className="py-3 px-4 text-[13px] text-text-secondary font-semibold text-left">Creditor Name</th>
                      <th className={classnames(accountsWideCell, 'py-3 px-4 text-[13px] text-text-secondary font-semibold text-left')}>Account #</th>
                      <th className="py-3 px-4 text-[13px] text-text-secondary font-semibold text-left">Balance</th>
                      <th className={classnames(accountsWideCell, 'py-3 px-4 text-[13px] text-text-secondary font-semibold text-left')}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {accounts.accounts.map((account) => (
                      <tr key={account._id} className="border-b border-divider last:border-0">
                        <td className="py-3 px-4 text-sm text-text-primary">
                          {account.creditorName || 'Unknown'}
                          {fixesOn && <span className="block pt-1 font-mono sm:hidden">{account.accountNumber}</span>}
                        </td>
                        <td className={classnames(accountsWideCell, 'py-3 px-4 text-sm text-text-primary font-mono')}>{account.accountNumber}</td>
                        <td className="py-3 px-4 text-sm text-text-primary font-medium">
                          {formatMoney(account.balanceAmount || 0)}
                          {fixesOn && (
                            <span className="block pt-2 sm:hidden">
                              <AccountStatusPill account={account} />
                            </span>
                          )}
                        </td>
                        <td className={classnames(accountsWideCell, 'py-3 px-4')}>
                          <AccountStatusPill account={account} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {/* === BUDGET SECTION === */}
          <section id="budget" className="mb-10">
            <h2 className="text-lg font-semibold text-text-primary mb-4">
              Budget
            </h2>

            <div className="border border-divider rounded-2xl p-5 bg-surface shadow-subtle dark:shadow-none mb-6">
              <p className="font-semibold text-[14px] text-text-primary mb-4">Program Overview</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-6">
                {[
                  { label: 'Program Length', value: budget?.schedule?.programLength ? (fixesOn ? paymentsLabel(budget.schedule.programLength) : `${budget.schedule.programLength} months`) : 'N/A' },
                  { label: 'Frequency', value: (fixesOn ? frequencyText(budget?.schedule?.frequency) : budget?.schedule?.frequency) || 'N/A', capitalize: true },
                  { label: 'Total Payment', value: budget?.schedule?.totalPayment ? formatMoney(budget.schedule.totalPayment) : 'N/A' },
                ].map((item) => (
                  <div key={item.label}>
                    <p className="text-[13px] text-text-secondary">{item.label}</p>
                    <p className={`text-lg font-semibold text-text-primary pt-1 ${item.capitalize ? 'capitalize' : ''}`}>
                      {item.value}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            <div className="border border-divider rounded-2xl p-5 bg-surface shadow-subtle dark:shadow-none">
              <p className="font-semibold text-[14px] text-text-primary mb-4">Debts Summary</p>
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <p className="text-[13px] text-text-secondary">Total Enrolled Debts</p>
                  <p className="text-lg font-semibold text-text-primary pt-1">
                    {budget?.schedule?.totalDebts ? formatMoney(budget.schedule.totalDebts) : 'N/A'}
                  </p>
                </div>
                <div>
                  <p className="text-[13px] text-text-secondary">Current Enrolled Total</p>
                  <p className="text-lg font-semibold text-text-primary pt-1">
                    {formatMoney(budget?.enrolledDebtsTotal || 0)}
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* === PAYMENTS SECTION === */}
          <section id="payments" className="mb-10">
            <h2 className="text-lg font-semibold text-text-primary mb-4">
              Payments
            </h2>

            {payments?.plan && (
              <div className="border border-divider rounded-2xl p-5 bg-surface shadow-subtle dark:shadow-none mb-6">
                <p className="font-semibold text-[14px] text-text-primary mb-2">Plan Details</p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <div>
                    <p className="text-[13px] text-text-secondary">Amount</p>
                    <p className="text-md font-medium text-text-primary">{formatMoney(payments.plan.amount)}</p>
                  </div>
                  <div>
                    <p className="text-[13px] text-text-secondary">Frequency</p>
                    <p className="text-md font-medium text-text-primary capitalize">{fixesOn ? frequencyText(payments.plan.frequency) : payments.plan.frequency}</p>
                  </div>
                  <div>
                    <p className="text-[13px] text-text-secondary">Start Date</p>
                    <p className="text-md font-medium text-text-primary">
                      {formatDateField(payments.plan.startDate, 'M/D/YYYY')}
                    </p>
                  </div>
                  <div>
                    <p className="text-[13px] text-text-secondary">Status</p>
                    <p className="text-md font-medium text-success-main capitalize">{payments.plan.status}</p>
                  </div>
                </div>
              </div>
            )}

            <PortalPaymentsTable
              title="Upcoming Payments"
              rows={payments?.upcoming}
              emptyLabel="No upcoming payments"
              mode={mode}
            />

            <PortalPaymentsTable
              title="Past Payments"
              rows={payments?.past}
              emptyLabel="No past payments"
              mode={mode}
            />
          </section>

          {/* === DOCUMENTS SECTION === */}
          <section id="documents" className="mb-10">
            <h2 className="text-lg font-semibold text-text-primary mb-4">
              Documents
            </h2>

            <div className={classnames('border border-divider rounded-2xl bg-surface shadow-subtle dark:shadow-none', tableCardOverflow)}>
              <table className="w-full">
                <thead className="bg-paper">
                  <tr>
                    <th className={classnames(documentsWideCell, 'py-3 px-4 text-[13px] text-text-secondary font-semibold text-left')}>Date</th>
                    <th className={classnames(documentsWideCell, 'py-3 px-4 text-[13px] text-text-secondary font-semibold text-left')}>Type</th>
                    <th className="py-3 px-4 text-[13px] text-text-secondary font-semibold text-left">Name</th>
                    <th className={classnames(documentsWideCell, 'py-3 px-4 text-[13px] text-text-secondary font-semibold text-left')}>Kind</th>
                    <th className="py-3 px-4 text-[13px] text-text-secondary font-semibold text-left">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {documents?.documents?.map((doc) => (
                    <tr key={doc._id} className="border-b border-divider last:border-0">
                      <td className={classnames(documentsWideCell, 'py-3 px-4 text-sm text-text-primary')}>
                        {documentDate(doc)}
                      </td>
                      <td className={classnames(documentsWideCell, 'py-3 px-4 text-sm text-text-primary')}>
                        {doc.type?.label || 'Document'}
                      </td>
                      <td className={fixesOn ? 'py-3 px-4 text-sm text-text-primary lg:max-w-[200px] lg:truncate' : 'py-3 px-4 text-sm text-text-primary max-w-[200px] truncate'}>
                        {doc.name || 'Untitled'}
                        {fixesOn && (
                          <span className="block pt-1 text-[13px] text-text-secondary lg:hidden">
                            {doc.type?.label || 'Document'} · {documentDate(doc)}
                            <DocumentKindPill doc={doc} className="ml-2" />
                          </span>
                        )}
                      </td>
                      <td className={classnames(documentsWideCell, 'py-3 px-4')}>
                        <DocumentKindPill doc={doc} />
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleDocView(doc._id, doc.name)}
                            className="text-sm text-primary-main hover:underline"
                          >
                            View
                          </button>
                          <button
                            onClick={() => handleDocDownload(doc._id, doc.name)}
                            className="text-sm text-primary-main hover:underline"
                          >
                            Download
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {(!documents?.documents || documents.documents.length === 0) && (
                <div className="py-10 text-center text-text-secondary text-sm">No documents available</div>
              )}
            </div>
          </section>

        </div>
      </div>

      {/* Request Modal */}
      <PortalRequestModal open={requestModalOpen} onClose={() => setRequestModalOpen(false)} />

      {/* Document View Modal */}
      <Modal open={docModalOpen} closeAfterTransition onClose={handleCloseDocModal}>
        <div className="flex items-center justify-center h-screen">
          <div className={docViewerBox}>
            <div className="absolute top-[20px] right-[20px] z-[999]">
              <div
                className="inline-flex justify-center items-center rounded-full cursor-pointer"
                onClick={handleCloseDocModal}
              >
                <LuX size={18} className="text-text-secondary" />
              </div>
            </div>
            <div className="overflow-auto" style={{ maxHeight: heightModalContent }}>
              {!docModalLoading && docModalContent && isImageFile(docModalFileType) ? (
                <div><img src={docModalContent} alt="Document" /></div>
              ) : !docModalLoading && docModalContent ? (
                <ViewPdf
                  width={fixesOn ? Math.min(800, window.innerWidth - 32) : 800}
                  height={heightModalContent}
                  pdf={docModalContent}
                  setups={false}
                />
              ) : null}
            </div>
            {docModalLoading && <Spinner />}
          </div>
        </div>
      </Modal>

      {/* Footer */}
      <div className="fixed bottom-0 left-0 right-0 bg-surface border-t border-divider px-10 py-3 z-10 flex items-center justify-between">
        {portalBrand.status === PORTAL_BRAND_STATUS.LEGACY ? (
          <div className="flex items-center gap-3">
            <span className="text-sm text-text-secondary">Need help?</span>
            <a href="mailto:support@mydebtlawyers.com" className="text-sm font-medium text-primary-main hover:underline">
              support@mydebtlawyers.com
            </a>
            <span className="text-sm text-text-secondary">|</span>
            <a href="tel:+13472187093" className="text-sm font-medium text-primary-main hover:underline">
              (347) 218-7093
            </a>
          </div>
        ) : (
          <PortalHelpLine portalBrand={portalBrand} />
        )}
        <button
          onClick={() => setRequestModalOpen(true)}
          className="bg-primary-main text-primary-contrast px-5 py-2 rounded-lg hover:opacity-90 transition-opacity text-sm font-medium"
        >
          Submit Request
        </button>
      </div>
    </>
  )
}

export default PortalDashboard
